import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { GraduationCap, Award, Briefcase } from "lucide-react";

/** Compact alumni-outcomes card for a single school. Rendered inside the Admin school-edit dialog.
 * Data endpoint: GET /api/admin/schools/{id}/alumni-outcomes */
export default function AlumniOutcomes({ schoolId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    api.get(`/admin/schools/${schoolId}/alumni-outcomes`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (!schoolId) return null;

  return (
    <div className="border-t border-[#EAE7E0] pt-4 mt-2" data-testid="alumni-outcomes-widget">
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-3 flex items-center gap-1.5">
        <GraduationCap size={11} /> Ringkasan alumni sekolah ini
        <span className="text-[10px] normal-case tracking-normal opacity-70">— internal only</span>
      </div>
      {loading ? (
        <div className="text-sm text-[#4B5563] py-3">Menghitung…</div>
      ) : !data || data.total_alumni_reviews === 0 ? (
        <div className="text-sm text-[#4B5563] py-3 bg-[#F3F1ED] rounded-xl px-3" data-testid="alumni-outcomes-empty">Belum ada review alumni untuk sekolah ini.</div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <StatCard label="Total review alumni" value={data.total_alumni_reviews} testid="alumni-total" />
            <StatCard label="Elite Alumni" value={data.elite_alumni_count} accent="coral" testid="alumni-elite" icon={<Award size={12} />} />
            <StatCard label="Rasio elite" value={`${Math.round(data.elite_alumni_ratio * 100)}%`} testid="alumni-elite-ratio" />
          </div>
          {data.top_study_destinations?.length > 0 && (
            <ListBlock title="Tujuan studi terpopuler" icon={<GraduationCap size={11} />} items={data.top_study_destinations} testidPrefix="alumni-study" />
          )}
          {data.top_professions?.length > 0 && (
            <ListBlock title="Profesi terpopuler" icon={<Briefcase size={11} />} items={data.top_professions} testidPrefix="alumni-profession" />
          )}
          {data.cohorts?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5">Kohort per 5 tahun</div>
              <div className="flex flex-wrap gap-1.5" data-testid="alumni-cohorts">
                {data.cohorts.map((c) => (
                  <span key={c.cohort} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F3F1ED] border border-[#EAE7E0] text-[11px] text-[#1F2937]">
                    {c.cohort} <b>· {c.count}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, icon, testid }) {
  const color = accent === "coral" ? "text-[#7C2D12] bg-[#FEF3E7] border-[#FED7AA]" : "text-[#1E3A8A] bg-white border-[#EAE7E0]";
  return (
    <div className={"p-2.5 rounded-xl border " + color} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">{icon}{label}</div>
      <div className="font-serif-display text-xl mt-0.5">{value}</div>
    </div>
  );
}

function ListBlock({ title, icon, items, testidPrefix }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1.5 flex items-center gap-1">{icon} {title}</div>
      <div className="space-y-1">
        {items.slice(0, 5).map((it, i) => (
          <div key={it.label} data-testid={`${testidPrefix}-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-[#FAFAF8] border border-[#EAE7E0]">
            <span className="text-sm text-[#1F2937] truncate">{it.label}</span>
            <span className="text-xs text-[#4B5563] tabular-nums">×{it.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
